/**
 * The eighth live route (#30 / R7.2) — a child's evening, on real data. READ HALF ONLY.
 *
 * **Two reads for an ordinary sign-in, and the first one is not optional.** (A family turn reads one
 * member's day instead: the last paragraph.) `GET /api/dashboard-data` returns every
 * active member for an admin and only the caller's row for a non-admin — and it never says which
 * row is the caller. The service KNOWS who called — it filters on `currentUserId` at
 * `dashboard.service.js`'s `currentUserId` filter — but the payload never says so: `meta` carries `timestamp`,
 * `timezone` and `isAdmin` and nothing else (that function's `meta` block). Guessing "the
 * only row" is right for a child and wrong for a parent, who would be shown somebody else's
 * quests. So this asks `GET /api/auth/verify` first, which returns `user.id`, and finds its own
 * row. A payload with no matching row is a FAILED READ, not an empty evening.
 *
 * **What this screen would say if you just handed it the payload.** Three claims this screen's
 * data does not back:
 *
 * | claim | what exists |
 * | --- | --- |
 * | a glance card reading "— in your wallet" | `server/routes/wallet.routes.js` exists and is mounted at `/api/wallet` (`server/index.js`), and `/wallet` has a live container. This screen does not wire the wallet glance card to it yet, so the card stays hidden |
 * | a glance card reading "— badges this week" | nothing on this endpoint counts badges, per week or otherwise |
 * | a "Tomorrow" section | `dashboard.service.js`'s due-chore queries selects only chores ALREADY DUE. There is no future schedule in the payload, and the upcoming renderer defaults `when` to the literal string 'tomorrow' |
 *
 * Plus `Today.jsx`'s `greetingWord` default, whose `greetingWord` DEFAULTS to "Good evening" — a hardcoded word plus the browser's
 * clock — false at breakfast, and false again for a family in another zone. The time of day IS
 * knowable, because `meta.timezone` comes from the server, so it is derived rather than dropped.
 * With no zone there is no time word at all.
 *
 * And the empty state, which reads *"Every lantern's lit ✦ — the village is glowing because of
 * you"*. That congratulates a child for finishing everything when the truth is usually that
 * nothing was due. Most evenings reach that branch.
 *
 * **The WRITE half is wired here (R7.2, second PR).** A tap sends
 * `POST /api/chores/:id/execute`; the 30-second reversal sends
 * `POST /api/chores/executions/:id/undo`. Both take an empty body — `executeChoreSchema` has
 * only optional fields and the undo route reads none.
 *
 * **The sentence this screen picks is a claim about money.** A completion on an approval-gated
 * quest is a REQUEST, not a payment: `chore.service.js` writes a pending `approvals` row and a
 * parent decides. The ritual renders "Awaiting approval" for `pending` and "Completion saved"
 * for `done`, so picking wrong tells a child money is theirs while a parent still holds it.
 * Nothing this container can read says which — `/api/dashboard-data` carries no
 * `requires_approval` per chore — so the completion RESPONSE now answers it, and this maps that
 * answer. It fails CLOSED, exactly like `approvalService.needsParentApproval`: only an explicit
 * `false` means saved.
 *
 * **No money is rendered.** Each chore carries a `reward` number and this endpoint sends no
 * currency, so the quest cards are given no reward at all rather than a figure in an invented
 * unit.
 *
 * **The vanishing-warning trap**, the same one every live route here documents: going live
 * leaves fixture mode, which silently REMOVES `SnapshotNotice`.
 *
 * **A family turn (#580 D1, #902) reads one member's day, never the family's.** During a child's turn
 * on a parent's device the session is the PARENT's: verify's `user.id` is the parent's and the
 * dashboard is the parent's whole family, which the turn may not read. The member and the turn's
 * generation come from the actor context's `familyTurn`, and the day from `GET /api/my-day/:userId`
 * (`toTodayFromMyDay`). What the screen holds is tagged with the turn it was read for, and only that
 * turn's answers are drawn, from the first render that names a new turn.
 */
import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, api } from '../api/client.js';
import { greetingWordFor, toTodayForMember } from '../lib/dashboardMapping.js';
import { STORY_LEVEL_UNAVAILABLE, toStoryLevelView, toStreakView } from '../lib/trophiesMapping.js';
import { notePending, notePendingRows, reconcileReviews, toReviewAnswer } from '../lib/approvalEcho.js';
import { turnSubject } from '../lib/familyTurnSubject.js';
import { reportFamilyTurn, reportTurnRefusal, turnReadMessage, turnReadStatus } from '../lib/familyInteraction.js';
import ApprovalEcho from '../components/ApprovalEcho.jsx';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { freshnessLead, useLiveRefresh, useReviewMemory } from '../state/HouseholdRealtime.jsx';

const Today = lazy(() => import('../pages/Today.jsx'));

/**
 * Carried on the page, because leaving fixture mode removes the fixture banner. It opens with
 * `freshnessLead(status)`: "Live family data." only while the screen is live (#580).
 */
export const NOT_BUILT_NOTICE =
  'Your wallet, your badges and tomorrow’s quests are not built into this screen yet, so those '
  + 'parts are not shown.';

/**
 * Said while a read is still in flight or has failed.
 *
 * The ready note opens with "Live family data." while live, which is a claim, and it is false at
 * the exact moment no data has arrived. The rest of the sentence is true in every state.
 */
export const NOT_LIVE_YET = NOT_BUILT_NOTICE;

/** An empty quest list is an answer: nothing is due. It is not "you finished everything". */
export const NOTHING_DUE = 'Nothing is due right now.';
export const EMPTY_TITLE = 'Nothing due right now ✦';
export const EMPTY_BODY =
  'Nothing is due right now. A quest appears here when its day arrives — this screen shows what '
  + 'is due, not everything you have ever finished.';

/**
 * What this list actually is.
 *
 * The page defaults to "Tonight's quests" and "N quests to light tonight". This payload has no
 * evening in it — `dashboard.service.js`'s due-chore queries selects chores ALREADY DUE, at 08:00 as readily
 * as at 20:00. Telling a child at breakfast that there are "2 quests to light tonight" hands
 * them a schedule nobody sent. Found by the gpt-6-astra review at a faked 08:00 local, which
 * read the heading, the subtitle AND the section's accessible name.
 */
export const QUESTS_HEADING = 'Due now';
export const QUESTS_SECTION_LABEL = 'Quests due now';
export function unlitSubtitle(count) {
  return `${count} quest${count === 1 ? '' : 's'} due now — the village is waiting.`;
}

/**
 * Said once every due quest is lit.
 *
 * The page's own default is "All lit tonight", which is the same hardcoded evening the heading
 * and the subtitle above already had removed. Found by an independent review at 08:00 local:
 * completing the only due quest put "Good morning" and "All lit tonight" on screen together.
 */
export const ALL_LIT_SUBTITLE = 'All lit — the village is glowing ✦';

/**
 * The greeting before anything is known.
 *
 * `greeting={undefined}` re-selects the page's own `"Good evening ✦"`, which is a claim about
 * the time of day made before any family zone was read. Both the loading state and a failed
 * identity read reached it. Found by the gpt-6-astra review, which asserted the exact `h1` in
 * each state. This container never hands the page `undefined` for that prop.
 *
 * It also makes NO time claim of its own. The first repair used "Your evening ✦", which is the
 * same defect one word smaller — an evening asserted before any family zone was read. The
 * gpt-5.6-sol verification caught it, and noted that the new guards only rejected
 * `/Good (morning|afternoon|evening)/` and so could not see it.
 */
export const GREETING_UNKNOWN = 'Your quests ✦';

/** Said when the streak read failed or could not be read. Never a number standing in for unknown. */
export const STREAK_UNAVAILABLE = 'Streak: not available right now.';

export const LOAD_FAILED =
  "We couldn't load your quests just now — give it another moment.";

/**
 * A 200 whose body could not be read for this member.
 *
 * That includes a family payload with no row for the signed-in member, which would otherwise
 * take the same branch as "nothing is due" — a statement about a child the read never described.
 */
export const LOAD_UNREADABLE =
  'Your quests came back in a shape we did not expect. Trying again may not help — please tell '
  + 'us if it keeps happening.';

/** #902: a family turn with no live member (locked, revoked or unreadable) names nobody to read for. */
export const NO_IDENTITY = 'We couldn’t tell whose quests to show just now.';

/**
 * #902: `meta.truncated` on the member's day. The server sets it for any omission, a dropped badge
 * included, so the note claims only that the list may be incomplete: "N quests due now" would then
 * count only what fitted.
 */
export const TRUNCATED_NOTE = 'Some quests may not be shown; the most overdue come first.';

/**
 * One sentence for a failed read.
 *
 * In an ordinary session both reads are signed-in-only and nothing more (`dashboard.routes.js`'s `/dashboard-data` route uses `requireAuth`;
 * `server/auth/routes/auth.routes.js`'s `/verify` route uses `verifyToken`), so every
 * signed-in member may make them and a retry is a door that can actually open. A family turn refuses
 * the dashboard, and its retry reads the member's own day (`readToday`), which the turn opens.
 *
 * #1003: `inTurn`, the failed read was a family turn's. Its refusal of the member's own day is not a door that opens
 * by waiting: the turn opens that day whenever it is live, so a refusal means it has ended, been replaced or been
 * revoked, and the sentence says so (`turnReadMessage`), never "give it another moment".
 */
export function loadMessage(error, inTurn = false) {
  if (error && error.noIdentity) return NO_IDENTITY;
  const turnStatus = inTurn ? turnReadStatus(error) : 'failed';
  if (turnStatus !== 'failed') return turnReadMessage(null, turnStatus === 'forbidden' ? 'locked' : turnStatus);
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  return LOAD_FAILED;
}

/**
 * #902: the turn's member as this screen reads a day for it (`lib/familyTurnSubject.js`). #903: with the
 * turn's generation, which its execute and undo send.
 */
const dayMember = (member, familyTurn) => ({
  id: member.userId, tenantId: familyTurn.household?.tenantId ?? null, generation: familyTurn.generation,
});

/**
 * #903: a write in a family turn, and the member's own day read, name the turn's generation (`X-Family-Actor`);
 * outside one, nothing.
 */
const familyWrite = (generation) => (generation ? { familyGeneration: generation } : undefined);

/**
 * #1003: the sentence for a family turn's refusal of one of Today's own secondary reads (`kind`), or `null` when
 * `error` is not a turn refusal, so an ordinary failure keeps its own wording.
 */
function turnRefusalNote(kind, error) {
  const status = turnReadStatus(error);
  return status === 'failed' ? null : turnReadMessage(kind, status);
}

/**
 * #903: when the server's undo deadline falls on this page's `performance.now()` clock. A family
 * completion's receipt carries it (`chore.service.js` `completionReceipt`): the server's own window,
 * deadline minus its completion time, counted from the attempt's FIRST send, so neither a slow answer nor a
 * replayed one adds its transit time (design §3: no extension for network delay). `undefined` without one.
 */
function undoUntilOf(receipt, firstSentAt) {
  const span = Date.parse(receipt.undo_deadline) - Date.parse(receipt.completed_at);
  return Number.isFinite(span) ? firstSentAt + span : undefined;
}

/**
 * Who am I, then what is my family's day; or, in a family turn, the member's own day. Each answer
 * carries the turn it was read for. The order matters: without the id there is no way to pick a row
 * out of an admin payload.
 */
function readToday(signal, subject) {
  // A background re-read (`onCommit`) has no signal. The account's reads then ask with the path alone, as they always have.
  const get = (path) => (signal ? api.get(path, { signal }) : api.get(path));
  if (!subject) {
    return get('/api/auth/verify')
      .then((me) => get('/api/dashboard-data').then((family) => ({ me, family, turnKey: null })));
  }
  if (!subject.member) {
    return Promise.reject(Object.assign(new Error('the family turn names no member'), { noIdentity: true }));
  }
  // #903: the member's own day refused means the turn is over: expired, or ended by another tab on the
  // same cookie, which no socket tells this one. Every read here asks it, the 30-second re-read included. It names
  // the turn's generation (`familyOwnRead`), so another tab picking the same child again refuses it too.
  const turn = familyWrite(subject.member.generation);
  return api.get(`/api/my-day/${encodeURIComponent(String(subject.member.id))}`, signal ? { ...turn, signal } : turn)
    .then((day) => ({ me: { user: subject.member }, day, turnKey: subject.key }), (error) => {
      reportTurnRefusal(subject.member.generation, error);
      throw error;
    });
}

/**
 * #902: `GET /api/my-day/:userId` (`server/services/my-day.service.js` `getMyDay`) in the shape
 * `toTodayForMember` gives. That list also holds what the member finished today (the service's union),
 * so only what is still due becomes a quest, in the server's order: outstanding, most overdue first.
 * Fails closed the same way: a day for another member, or with no list, is unreadable.
 */
function toTodayFromMyDay(payload, memberId) {
  if (!payload || typeof payload !== 'object' || !payload.user || !Array.isArray(payload.quests)) return null;
  if (memberId == null || String(payload.user.id) !== String(memberId)) return null;
  const text = (value) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);
  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
  return {
    name: text(payload.user.firstName),
    quests: payload.quests
      .filter((q) => q && q.completed !== true)
      .map((q) => ({ id: q.id, title: text(q.name) || 'Quest', status: 'unlit' })),
    timezone: text(meta.timezone),
    truncated: meta.truncated === true,
  };
}

/** This member's day from whichever read produced it, or `null` when it does not describe them. */
function todayOf(data) {
  const user = data && data.me && data.me.user;
  if (!user) return null;
  const id = user.id != null ? user.id : user.userId;
  return data.day !== undefined ? toTodayFromMyDay(data.day, id) : toTodayForMember(data.family, id);
}

/** What the screen draws while the turn on screen has no answer of its own yet. */
const NOT_YET = Object.freeze({ status: 'loading', data: null, error: null });

/**
 * One id per completion ATTEMPT.
 *
 * `crypto.randomUUID` is present in every browser this app supports and in the test
 * environment. The fallback exists so a missing `crypto` degrades to a duplicate-key 409, which
 * is loud, rather than to a thrown `TypeError` before the request is even sent.
 */
/**
 * #580: how long a completion may hold live re-reads off, at most.
 *
 * A just-lit quest leaves the due list the moment the server records it, so a re-read inside the
 * child's undo window would take the quest — and its Undo — off the screen. Live re-reads therefore
 * wait while a completion is in flight or its window is open. The hold ends when the window closes
 * (`onCommit`, which re-reads anyway) or an undo settles. This cap only stops a hold whose window
 * never reported back from pausing the screen for good; it is longer than any undo window.
 */
const LIVE_HOLD_CAP_MS = 120_000;

function newAttemptId() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function TodayRoute() {
  const subject = turnSubject(useOptionalActor()?.familyTurn, dayMember);
  const turnKey = subject ? subject.key : null;
  const subjectRef = useRef(subject);
  subjectRef.current = subject;
  const [stored, setState] = useState(() => ({ ...NOT_YET, turnKey }));
  // Only what was read for the turn on screen. The effect below starts the new turn's read after this
  // render has committed, so without this the first frame of a new turn drew the last one's quests.
  const state = stored.turnKey === turnKey ? stored : NOT_YET;
  const [reloadKey, setReloadKey] = useState(0);

  /**
   * Counts every background refresh. A refresh answer may only land if no later refresh has
   * started since it went out.
   *
   * Without it, two completions one window apart start two refreshes, the newer answer arrives
   * first, and the older one then puts a completed quest back on screen. An outstanding
   * background answer could also overwrite a successful retry of the main read. Both were
   * reproduced by an independent review.
   */
  const readSeq = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    // A reload alone needed no sequence bookkeeping — that survived its mutation, twice. A reload
    // needs the retry control, which needs an error, and a background failure never sets one; a
    // refresh only follows a completion, which needs quests on screen. A NEW FAMILY TURN is different
    // (#902): a refresh read for the last turn can be in flight when it starts, so this read retires
    // every one of them. On a reload the bump changes nothing, by the reasoning above.
    readSeq.current += 1;
    setState({ status: 'loading', data: null, error: null, turnKey });
    readToday(controller.signal, subjectRef.current)
      .then((data) => { if (live) setState({ status: 'ready', data, error: null, turnKey }); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error, turnKey });
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey, turnKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  /**
   * The streak, from S3's verified reader (#583/#584 N1 S7), for the member verify named (in a family
   * turn, the turn's member: #1003 opens this read for them, and it names the turn's generation, which the
   * server requires).
   *
   * Its OWN read, and allowed to fail on its own: a streak outage must not take the quests down.
   * `lib/trophiesMapping.js` decides what may be shown — a flame only for a current verified run,
   * "not current" for a stale one, the labelled earlier record while the switch is off.
   *
   * The view is stored WITH the member it was read for, and shown only while that is still the
   * member on screen. The reset inside the effect runs after a render, so without the id a member
   * switch (IC-01, below) rendered the previous member's flame under the new member's name. #1003: in a
   * family turn the key is the member AND the turn's generation, so the same child's earlier turn never paints.
   * A turn's refusal is said as such (`turnReadMessage`), never as a zero, and it reports the turn ended.
   */
  const memberId = state.data?.me?.user ? (state.data.me.user.id ?? state.data.me.user.userId) : null;
  const memberGeneration = state.data?.me?.user?.generation ?? null;
  const readKey = `${memberId}:${memberGeneration ?? ''}`;
  const [streakRefresh, setStreakRefresh] = useState(0);
  const [streakRead, setStreakRead] = useState({ key: null, view: null });
  useEffect(() => {
    if (!Number.isInteger(memberId)) return undefined;
    const controller = new AbortController();
    const key = `${memberId}:${memberGeneration ?? ''}`;
    setStreakRead({ key: null, view: null });
    api.get(`/api/streaks/${memberId}`, { signal: controller.signal, ...familyWrite(memberGeneration) })
      .then((s) => { if (!controller.signal.aborted) setStreakRead({ key, view: toStreakView(s) ?? 'unavailable' }); })
      .catch((error) => {
        if (controller.signal.aborted) return;
        reportTurnRefusal(memberGeneration, error);
        const refused = turnRefusalNote('streak', error);
        setStreakRead({ key, view: refused ? { refused } : 'unavailable' });
      });
    return () => controller.abort();
  }, [memberId, memberGeneration, reloadKey, streakRefresh]);
  const streakView = streakRead.key === readKey ? streakRead.view : null;
  const streakHeader = streakView && streakView !== 'unavailable' && !streakView.refused ? streakView.header : null;
  const streakNote = streakView === 'unavailable' ? STREAK_UNAVAILABLE
    : streakView?.refused ? streakView.refused : streakView ? streakView.sentence : null;

  /**
   * The story level, from S13's `GET /api/story-progress/:userId`, for the member verify named (in a family turn,
   * the turn's member, with the turn's generation, as the streak). A secondary line:
   * its own read, allowed to fail on its own, stored with the member it was read for (the streak's reasons above).
   * While the story switch is off the answer is `{ enabled: false }` and nothing is added to the screen. The raw
   * answer is kept, so its instant is formatted in the household zone once `model` knows it. A completion or an undo
   * re-reads it on the streak's trigger. An unrecognized route (404) predates S13: no line. A known missing member is unavailable.
   */
  const [levelRead, setLevelRead] = useState({ key: null, answer: null });
  useEffect(() => {
    if (!Number.isInteger(memberId)) return undefined;
    const controller = new AbortController();
    const key = `${memberId}:${memberGeneration ?? ''}`;
    setLevelRead({ key: null, answer: null });
    api.get(`/api/story-progress/${memberId}`, { signal: controller.signal, ...familyWrite(memberGeneration) })
      .then((p) => { if (!controller.signal.aborted) setLevelRead({ key, answer: p }); })
      .catch((error) => {
        if (controller.signal.aborted) return;
        reportTurnRefusal(memberGeneration, error);
        const refused = turnRefusalNote('level', error);
        setLevelRead({ key, answer: refused ? { refused }
          : error instanceof ApiError && error.status === 404 && error.body?.error !== 'No such family member' ? { enabled: false } : 'unavailable' });
      });
    return () => controller.abort();
  }, [memberId, memberGeneration, reloadKey, streakRefresh]);

  /**
   * The completion attempt in flight on each quest, `{ operationKey, generation }`, kept until it RESOLVES.
   *
   * This is what stops a lost response becoming a second completion. `chore.service.js`'s `if (operationKey)` replay branch
   * replays a completion when the same `operationKey` arrives again, and its own comment says
   * the duplicate "closes when the client sends a key". Sending `{}` meant every retry after a
   * timeout inserted another `chores_log` row and asked a parent for the same money twice —
   * found by an independent review, and reproduced.
   */
  const attemptKeys = useRef(new Map());

  /** Quest id → its hold on live re-reads, `{ until }`, which lapses then. See `LIVE_HOLD_CAP_MS`. */
  const liveHolds = useRef(new Map());
  /** Quest id → the undo in flight on it. */
  const undoInFlight = useRef(new Map());
  const liveHeld = useCallback(() => {
    const now = Date.now();
    for (const [id, hold] of liveHolds.current) {
      if (hold.until <= now && !undoInFlight.current.has(id)) liveHolds.current.delete(id);
    }
    return liveHolds.current.size > 0;
  }, []);
  const liveControl = useRef(null);
  const reviewsControl = useRef(null);
  /**
   * Ending a hold retires every read that went out during it, and asks again. A read that left
   * before an undo committed describes the board BEFORE the undo; publishing it would drop the
   * un-lit quest from the due list (review q1 H2).
   *
   * #903: given `hold`, only that hold ends. A late answer of an earlier family turn names the hold its own
   * call set, so it never ends the next turn's hold on the same quest, nor retires that turn's reads.
   */
  const releaseHold = useCallback((id, hold) => {
    if (hold !== undefined && liveHolds.current.get(id) !== hold) return;
    if (!liveHolds.current.delete(id)) return;
    readSeq.current += 1;
    if (!liveHeld()) {
      liveControl.current?.();
      reviewsControl.current?.();
    }
  }, [liveHeld]);

  /**
   * Light a quest for real.
   *
   * Three things have to be true before this reports a save, and the first version checked none
   * of them. A receipt is only a save when the server SAID it succeeded, when it describes the
   * quest that was tapped, and when it says who is holding the reward.
   *
   * `approval_pending === false` is the only way to reach `done`. Anything else — `true`, a
   * missing field, a body of the wrong shape — throws, and the child is told it could not be
   * confirmed. That is stricter than the first version, which mapped a missing field to
   * `pending` and so announced a parent nobody had asked. The client and the server ship in one
   * image, so a missing field is a broken contract rather than an old server, and inventing an
   * answer for it is how a false sentence about money gets on screen. Throwing is safe here
   * ONLY because of the operation key above: the child's retry replays, it does not re-charge.
   */
  const onComplete = useCallback(async (quest) => {
    // Reuse the attempt that never resolved: its key AND its turn. A resolved attempt is deleted
    // below, so the next tap on the same quest is a NEW operation and never replays this one.
    // #903: an attempt made in another family turn is never sent again, under either generation (design
    // §3: a pending request never gains a new generation); this tap starts its own (the kiosk's rule).
    const generation = subjectRef.current?.member?.generation ?? null;
    let attempt = attemptKeys.current.get(quest.id);
    if (!attempt || attempt.generation !== generation) {
      attempt = { operationKey: `today:${newAttemptId()}`, generation, firstSentAt: performance.now() };
      attemptKeys.current.set(quest.id, attempt);
    }
    // Only this attempt's own entry: a newer one for the same quest must survive this answer.
    const settle = () => { if (attemptKeys.current.get(quest.id) === attempt) attemptKeys.current.delete(quest.id); };

    const hold = { until: Date.now() + LIVE_HOLD_CAP_MS };
    liveHolds.current.set(quest.id, hold);
    let receipt;
    try {
      receipt = await api.post(`/api/chores/${quest.id}/execute`, { operationKey: attempt.operationKey }, familyWrite(attempt.generation));
    } catch (error) {
      releaseHold(quest.id, hold);
      // The turn is over, and the server wrote nothing: this attempt is finished.
      if (reportTurnRefusal(attempt.generation, error)) settle();
      throw error;
    }

    if (!receipt || receipt.success !== true) {
      releaseHold(quest.id, hold);
      throw new Error('The completion receipt did not report success.');
    }
    if (Number(receipt.chore_id) !== Number(quest.id)) {
      releaseHold(quest.id, hold);
      throw new Error('The completion receipt names a different quest.');
    }
    if (typeof receipt.approval_pending !== 'boolean') {
      releaseHold(quest.id, hold);
      throw new Error('The completion receipt did not say who is holding the reward.');
    }
    // NO local refusal when `execution_id` is missing. `lanternRitual.jsx`'s `No execution receipt` refusal already refuses a
    // receipt without one, and its bare `catch` swallows any message written here. Measured:
    // weakening the ritual's refusal is caught, and deleting a duplicate check here is not.
    // But that refusal opens NO undo window, so nothing would ever end this hold: end it here.
    if (receipt.execution_id == null || receipt.execution_id === '') releaseHold(quest.id, hold);

    setStreakRefresh((n) => n + 1);
    settle();
    // #903: the saved completion renewed the turn on the server (`renewOnWrite`).
    reportFamilyTurn(attempt.generation, { expiresAt: receipt.turn_expires_at });
    // #580 unit 11: a fast parent must not be missed because no review read landed in between.
    if (receipt.approval_pending) notePending(reviewMemoryRef.current, memberIdRef.current, Number(receipt.execution_id));
    return {
      executionId: receipt.execution_id,
      status: receipt.approval_pending ? 'pending' : 'done',
      undoUntil: undoUntilOf(receipt, attempt.firstSentAt),
    };
  }, [releaseHold]);

  /**
   * Reverse it, inside the 30-second window.
   *
   * `POST /executions/:id/undo` answers `{success, message, chore_id, user_id}` and NO execution
   * id, so the ritual's `result.executionId !== record.executionId` check has nothing of its own
   * to compare. Echoing back the id we sent would satisfy that check without proving anything.
   * So the receipt is checked against `chore_id`, which is the one identifier the server DID
   * send, and only then is the id echoed.
   */
  const onUndo = useCallback(async (quest, { executionId }) => {
    const generation = subjectRef.current?.member?.generation ?? null;
    // #903: what this undo owns: the completion's hold it ends, and its own entry. Never a newer turn's.
    const hold = liveHolds.current.get(quest.id) ?? null;
    const mine = {};
    undoInFlight.current.set(quest.id, mine);
    try {
      const receipt = await api.post(`/api/chores/executions/${executionId}/undo`, {}, familyWrite(generation))
        .catch((error) => { reportTurnRefusal(generation, error); throw error; });
      if (!receipt || receipt.success !== true) {
        throw new Error('The reversal receipt did not report success.');
      }
      if (Number(receipt.chore_id) !== Number(quest.id)) {
        throw new Error('The reversal receipt names a different quest.');
      }
      reportFamilyTurn(generation, { expiresAt: receipt.turn_expires_at });
      setStreakRefresh((n) => n + 1);
      return { executionId, status: 'unlit' };
    } finally {
      // The ritual will not report this window closing once an undo has been pressed.
      if (undoInFlight.current.get(quest.id) === mine) undoInFlight.current.delete(quest.id);
      releaseHold(quest.id, hold);
    }
  }, [releaseHold]);

  /**
   * The undo window closed, so the completion is final. Re-read, and do it QUIETLY.
   *
   * `reload()` would be wrong here. It sets `data` to null (#902: the main read starts every
   * turn and every reload from nothing), so the quest list disappears while the read is in
   * flight. And it routes a FAILED background read into `status: 'error'`, which wipes a working
   * screen and tells the child their quests could not be loaded — while the quests were on
   * screen and nothing on it became false.
   *
   * Three conditions before this replaces anything, and the first version had none of them.
   * The answer must be the newest one asked for. It must be READABLE for this member —
   * `toTodayForMember` returning `null` would otherwise turn a working screen into "your quests
   * came back in a shape we did not expect". And a failure must change nothing at all.
   *
   * No mounted-ref guard. React 19 dropped the unmounted-setState warning and the call is a
   * no-op, so the ref guarded nothing — and the version written here had a StrictMode bug of
   * its own, because a remount never set it back to `true`.
   */
  const onCommit = useCallback((quest) => {
    // This read is the reconciliation the hold was waiting for, so nothing deferred is re-asked.
    liveHolds.current.delete(quest?.id);
    // Re-read at the original deadline before announcing a decision seen during the Undo window (§4).
    reviewsControl.current?.();
    const seq = (readSeq.current += 1);
    readToday(undefined, subjectRef.current)
      .then((data) => {
        // Something newer has been asked for since, a new family turn's read included. A stale
        // answer is not an improvement.
        if (seq !== readSeq.current) return;
        // Unreadable FOR THIS MEMBER. On the main read that is an honest failure to report.
        // In the background it is only a reason to keep what is already true on screen.
        if (!todayOf(data)) return;
        setState({ status: 'ready', data, error: null, turnKey: data.turnKey });
      })
      .catch(() => { /* keep the screen as it is; nothing on it became false */ });
  }, []);

  /**
   * #580: re-read when the household changes, on the same terms as `onCommit` — quietly, only a
   * readable answer for this member replaces the screen, and a newer read of either kind wins. It
   * waits while the child has a completion open (`LIVE_HOLD_CAP_MS`).
   */
  const live = useLiveRefresh('today', {
    read: (signal) => {
      const seq = (readSeq.current += 1);
      return readToday(signal, subjectRef.current).then((data) => ({ seq, data }));
    },
    apply: ({ seq, data }) => {
      if (liveHeld()) return 'deferred';
      if (seq !== readSeq.current) return 'deferred'; // an `onCommit` read or a new turn's went out since, and it wins
      if (!todayOf(data)) return false;
      setState({ status: 'ready', data, error: null, turnKey: data.turnKey });
      return true;
    },
    enabled: state.status === 'ready',
  });
  liveControl.current = live.invalidate;

  /**
   * #580 unit 11: the child's approval echo. Its own read, `GET /api/completion-reviews` — the signed-in
   * member's OWN reviews, so a parent's Today never learns of a child's — re-read on the same events and
   * recovery as the quests, and allowed to fail on its own (the quests stay; the echo just waits).
   *
   * The echo comes from what that read SAYS, compared with what this tab last knew (`lib/approvalEcho.js`):
   * only a completion it knew as pending and now reads approved or held is announced, once. No event payload
   * reaches it. Reviews update a matching local receipt immediately and remove now-invalid Undo. Only the
   * echo waits for the original window to close and another authoritative read (`onCommit`).
   */
  const reviewMemory = useReviewMemory();
  const reviewMemoryRef = useRef(reviewMemory);
  reviewMemoryRef.current = reviewMemory;
  const memberIdRef = useRef(memberId);
  memberIdRef.current = memberId;
  // The echo is stored with the member it was read for, and only ever drawn for that member (L2: the reset
  // below runs AFTER a member switch commits, so without the key one commit showed the last member's echo).
  const [echo, setEcho] = useState(null);
  const [reviewRead, setReviewRead] = useState(null);
  // #1003: a family turn's refusal of the echo read, said once (`turnReadMessage`), for the identity it was read for.
  const [echoRefusal, setEchoRefusal] = useState(null);
  const identityKeyRef = useRef(null);
  const reviewsLive = useLiveRefresh('today', {
    // #1003: in a family turn the read names the turn's generation, taken when it leaves. A refusal stops the
    // re-reads (`useLiveRefresh` stops on a 403), reports the turn ended, and is said, never retried.
    read: (signal) => {
      const generation = subjectRef.current?.member?.generation ?? null;
      const key = identityKeyRef.current;
      return api.get('/api/completion-reviews', { signal, ...familyWrite(generation) })
        .then((raw) => ({ raw, generation }), (error) => {
          if (!signal.aborted) {
            reportTurnRefusal(generation, error);
            const refused = turnRefusalNote('echo', error);
            if (refused) setEchoRefusal({ key, sentence: refused });
          }
          throw error;
        });
    },
    apply: ({ raw, generation }) => {
      // Read for another turn than the one on screen: never drawn, and never remembered.
      if (generation !== (subjectRef.current?.member?.generation ?? null)) return 'deferred';
      const reviewAnswer = toReviewAnswer(raw, memberIdRef.current);
      if (!reviewAnswer) return false;
      setEchoRefusal(null);
      setReviewRead({ key: identityKeyRef.current, reviews: reviewAnswer.reviews });
      // The ritual applies the status now; keep only the announcement pending until its original deadline.
      // What this read reports pending is still remembered, so its later decision is a transition seen (F1).
      if (liveHeld()) {
        notePendingRows(reviewMemoryRef.current, reviewAnswer);
        return true;
      }
      const found = reconcileReviews(reviewMemoryRef.current, reviewAnswer);
      if (found.approved.length + found.held.length > 0) setEcho({ key: identityKeyRef.current, found });
      return true;
    },
    enabled: state.status === 'ready' && Number.isInteger(memberId) && reviewMemory !== null,
    requireCleanRead: true,
  });
  reviewsControl.current = reviewsLive.invalidate;

  const model = useMemo(() => todayOf(state.data), [state.data]);

  /**
   * WHO this screen is for: household and member, from the same identity read as `model`.
   *
   * IC-01 (#580). A background re-read (`onCommit`) can come back as a DIFFERENT member, because
   * every tab shares one cookie. `Today` keeps its lanterns by quest id, and so does
   * `attemptKeys`, and a chore two members share has one id. Without this, the next member saw
   * the last one's open undo and resent the last one's retry key, which the server then refuses
   * as a different completion. Keying `Today` on this remounts it clean; the effect below drops
   * the retry keys. The DAY is deliberately not part of it: a retry key must survive midnight.
   */
  const identityKey = useMemo(() => {
    const user = state.data && state.data.me && state.data.me.user;
    if (!user) return 'nobody';
    const id = user.id != null ? user.id : user.userId;
    // #903: and the family turn's generation, so the same child picked again gets a clean page too: no
    // receipt, Undo or retry key of the earlier turn reaches the next (design §3).
    return `${user.tenantId ?? ''}:${id}${user.generation ? `:${user.generation}` : ''}`;
  }, [state.data]);
  identityKeyRef.current = identityKey;
  const echoForMember = echo && echo.key === identityKey ? echo.found : null;
  const echoNote = echoRefusal && echoRefusal.key === identityKey ? echoRefusal.sentence : null;
  // A LAYOUT effect, so the drop happens inside the commit that changes the member, before any tap
  // can be handled. As a passive effect it ran after that commit, and a first tap in the gap made
  // its retry key and then lost it: the retry after a lost response sent a NEW key, a second
  // completion. CI hit it under load on PR #949; `TodayRoute.test.jsx` enters the gap on purpose.
  useLayoutEffect(() => {
    attemptKeys.current.clear();
    liveHolds.current.clear();
    undoInFlight.current.clear();
    setEcho(null);
  }, [identityKey]);
  // The FIRST member keeps the page that loaded for them. `nobody` → member is not a member
  // change, and remounting on it threw away the `<main>` a skip link had just focused (E2E
  // flakes, 2026-09-30). Any member after the first still gets a clean page.
  const [firstMember, setFirstMember] = useState(null);
  if (firstMember === null && identityKey !== 'nobody') setFirstMember(identityKey);
  const first = firstMember ?? (identityKey === 'nobody' ? null : identityKey);
  const pageKey = identityKey === 'nobody' || identityKey === first ? 'first' : identityKey;

  /**
   * A payload this container cannot read FOR THIS MEMBER is a failed read.
   *
   * `toTodayForMember` returns `null` both for a malformed body and for a family that contains
   * no row for the signed-in member. Letting either share a branch with "nothing is due" would
   * put a sentence about this child's evening on screen from a read that never described them.
   */
  const unreadable = state.status === 'ready' && model === null;
  const loadError = state.status === 'error'
    ? loadMessage(state.error, state.turnKey !== null)
    : unreadable ? LOAD_UNREADABLE : null;
  const ready = state.status === 'ready' && !unreadable;
  const hasQuests = !!(model && model.quests.length > 0);
  const levelAnswer = levelRead.key === readKey ? levelRead.answer : null;
  const levelView = levelAnswer !== null && !levelAnswer?.refused
    ? (levelAnswer === 'unavailable' ? null : toStoryLevelView(levelAnswer, model ? model.timezone : null))
    : undefined;
  const levelNote = levelAnswer?.refused ? levelAnswer.refused
    : levelView === undefined || levelView?.off ? null : levelView === null ? STORY_LEVEL_UNAVAILABLE : levelView.sentence;

  const notice = [freshnessLead(live.status), ready && model.truncated ? TRUNCATED_NOTE : '', NOT_BUILT_NOTICE]
    .filter(Boolean).join(' ');
  const note = !ready
    ? NOT_LIVE_YET
    : hasQuests ? notice : `${NOTHING_DUE} ${notice}`;

  // The word comes from the FAMILY's clock. With no zone there is no word, and the greeting is
  // the name alone rather than a guess about where they are.
  const word = model ? greetingWordFor(model.timezone) : null;
  // NEVER `undefined` — that re-selects the page's hardcoded "Good evening ✦" before any zone
  // has been read, which is exactly the claim this container exists to remove.
  const greeting = model && model.name
    ? (word ? `${word}, ${model.name} ✦` : `${model.name} ✦`)
    : (word ? `${word} ✦` : GREETING_UNKNOWN);

  return (
    <Suspense fallback={<LanternLoader label="Loading tonight’s quests…" />}>
      <Today
        key={pageKey}
        showSnapshotNotice={false}
        // `greeting` overrides the whole hero line, so `greetingWord` and `kidName` never get a
        // chance to compose "Good evening ✦" out of a hardcoded word and an empty name.
        greeting={greeting}
        kidName={model && model.name ? model.name : ''}
        tonight={model ? model.quests : []}
        // NOT `[]` meaning "I read tomorrow and it was empty". There is no tomorrow in this
        // payload at all, and the page renders nothing for an empty list either way.
        tomorrow={[]}
        // `false`, NOT `null`. `null` means "wired, value unknown" and still draws a card
        // reading "— in your wallet". Neither card is wired here: `/api/wallet` exists, but this
        // screen does not read it yet, and nothing counts badges per week.
        walletBalance={false}
        badgesThisWeek={false}
        // Only a verified, current run lights the flame; everything else is the sentence below.
        streak={streakHeader}
        streakNote={streakNote}
        levelNote={levelNote}
        echo={(
          <>
            {echoNote ? <p className="tdy-note" data-part="echo-note">{echoNote}</p> : null}
            <ApprovalEcho echo={echoForMember} onDismiss={() => setEcho(null)} />
          </>
        )}
        reviews={reviewRead?.key === identityKey ? reviewRead.reviews : undefined}
        note={note}
        questsHeading={QUESTS_HEADING}
        questsSectionLabel={QUESTS_SECTION_LABEL}
        unlitSubtitle={unlitSubtitle}
        allLitSubtitle={ALL_LIT_SUBTITLE}
        // The page's own default names an evening the payload does not describe.
        loadingLabel="Waiting for your quests; the list is not known yet."
        errorLabel="We couldn’t load your quests just now — give it another moment." 
        emptyTitle={EMPTY_TITLE}
        emptyBody={EMPTY_BODY}
        loading={state.status === 'loading'}
        error={loadError}
        onRetry={reload}
        // Supplying `onComplete` is what takes the ritual OUT of preview mode. Once it is here,
        // every "not saved" sentence on this screen is a lie and has to go with it.
        onComplete={onComplete}
        onUndo={onUndo}
        onCommit={onCommit}
      />
    </Suspense>
  );
}
